import type en from "../en/builder";
import type { Messages } from "../../types";

const fr: Messages<typeof en> = {
  // Server action messages
  errUnknownPage: "Page inconnue",
  errInvalidContent: "Contenu de page invalide",
  errInvalidSettings: "Paramètres invalides",
  errUnknownRevision: "Révision inconnue",
  errUnknownMenu: "Menu inconnu",
  errInvalidMenu: "Menu invalide",
  errUnknownFile: "Fichier inconnu",
  errAltTooLong: "Le texte alternatif ne doit pas dépasser 300 caractères.",
  errTooLong: "Trop long",
  publishedLive: "« {title} » et les styles de votre thème sont maintenant en ligne.",
  themeChanged: "Thème modifié. Publiez pour le mettre en ligne.",
  pageSettingsSaved: "Paramètres de la page enregistrés.",
  unpublishedMsg: "Dépubliée",
  draftDiscarded: "Brouillon annulé",
  revisionRestored: "Restauré dans le brouillon. Vérifiez-le dans le constructeur, puis publiez.",
  markedReviewed: "Marqué comme vérifié",
  menuSaved: "Menu enregistré — il est en ligne sur votre boutique.",
  altSaved: "Texte alternatif enregistré",

  // Builder page / editor
  noPagesYet: "Aucune page pour l’instant",
  noPagesBuilderDesc: "Créez d’abord une page, puis concevez-la ici.",
  goToPages: "Aller aux pages",
  exitBuilder: "Quitter le constructeur",
  pageToEdit: "Page à modifier",
  homeSuffix: "Accueil",
  previewSize: "Taille de l’aperçu",
  previewDesktop: "Aperçu ordinateur",
  previewTablet: "Aperçu tablette",
  previewMobile: "Aperçu mobile",
  undoShortcut: "Annuler (Ctrl+Z)",
  redoShortcut: "Rétablir (Ctrl+Maj+Z)",
  pageNotLive: "Cette page contient des modifications qui ne sont pas en ligne",
  themeNotLive: "Les styles du thème contiennent des modifications qui ne sont pas en ligne",
  notLiveYet: "Pas encore en ligne",
  previewNewTab: "Aperçu dans un nouvel onglet",
  notSaved: "Non enregistré : {error}",
  editorPanels: "Panneaux de l’éditeur",
  pageStructure: "Structure de la page et thème",
  editorTools: "Outils de l’éditeur",
  tabSections: "Sections",
  tabText: "Texte",
  tabTheme: "Thème",
  tabStyles: "Styles",
  pageText: "Texte de la page",
  pageTextHint: "Texte brut. Commencez une ligne par ## pour un titre et par - pour un élément de liste. Laissez une ligne vide entre les paragraphes.",
  policyTemplateNote: "Les modèles de politiques sont un point de départ, pas un avis juridique. Faites-les vérifier pour votre activité et les pays dans lesquels vous vendez.",
  livePreview: "Aperçu en direct",
  draftPreviewOf: "Aperçu du brouillon de",
  draftPreviewTitle: "Aperçu du brouillon de {title}",
  reload: "Recharger",
  sectionSettings: "Paramètres de la section",
  showSection: "Afficher la section",
  hideSection: "Masquer la section",
  sectionHiddenNote: "Cette section est masquée et n’apparaîtra pas sur votre boutique.",
  duplicate: "Dupliquer",
  deleteSection: "Supprimer la section",
  noSectionSelected: "Aucune section sélectionnée",
  noSectionSelectedHint: "Choisissez une section dans la liste pour modifier son texte, ses images et sa mise en page.",
  showSections: "Afficher les sections",
  deleteSectionConfirmTitle: "Supprimer cette section ?",
  deleteSectionConfirmBody: "La section {name} et son contenu seront retirés du brouillon. Vous pouvez annuler avec Ctrl+Z, ou plutôt masquer la section.",
  publishConfirmTitle: "Publier vos modifications ?",
  publishConfirmIntro: "Vos modifications sont enregistrées automatiquement dans un brouillon que vous seul voyez dans l’aperçu. La publication copie le brouillon sur votre boutique en ligne :",
  publishPageChanged: "La page « {title} » (modifications non publiées)",
  publishPageUnchanged: "La page « {title} » (aucune nouvelle modification)",
  publishThemeChanged: "Thème et styles (modifications non publiées)",
  publishThemeUnchanged: "Thème et styles (aucune nouvelle modification)",
  publishOtherPagesNote: "Les autres pages ayant un brouillon se publient séparément depuis l’écran Pages. Une révision est enregistrée à chaque publication, ce qui vous permet de restaurer des versions antérieures.",
  storeNotOpen: "Votre boutique elle-même n’est pas encore ouverte aux clients. Quand vous serez prêt, lancez-la depuis",
  dismiss: "Fermer",
  allSaved: "Toutes les modifications sont enregistrées en brouillon",
  unsavedChanges: "Modifications non enregistrées",
  couldntSave: "Échec de l’enregistrement",
  couldntSaveRetry: "Échec de l’enregistrement — réessayer",
  errSaveNetwork: "Impossible de joindre le serveur. Vérifiez votre connexion — nous conservons vos modifications ici.",
  confirmLeaveUnsaved: "Vos dernières modifications n’ont pas pu être enregistrées. Quitter cette page quand même ?",
  errSaveFirst: "Enregistrez d’abord vos modifications en cours (voir l’erreur en haut).",
  errNetworkRetry: "Impossible de joindre le serveur. Veuillez réessayer.",
  errPublishUnsaved: "Vos dernières modifications n’ont pas pu être enregistrées, rien n’a donc été publié. Corrigez l’erreur et réessayez.",
  errPublishNetwork: "Impossible de joindre le serveur. Rien n’a été publié — veuillez réessayer.",
  sectionAdded: "{name} ajoutée en position {position} sur {total}.",
  movedTo: "{name} déplacé en position {position} sur {total}.",
  sectionShown: "{name} affichée.",
  sectionHidden: "{name} masquée.",
  sectionDuplicated: "{name} dupliquée.",
  sectionDeleted: "Section supprimée. Appuyez sur Ctrl+Z pour annuler.",
  linkHome: "Accueil",
  linkShopAll: "Tous les produits",
  linkCollection: "Collection : {title}",
  linkPage: "Page : {title}",
  loadingPreview: "Chargement de l’aperçu",

  // Section list
  noSections: "Cette page n’a pas encore de section. Ajoutez-en une ci-dessous.",
  pageSections: "Sections de la page",
  dragToReorder: "Glisser pour réorganiser",
  hidden: "Masquée",
  moveUp: "Monter {name}",
  moveDown: "Descendre {name}",
  showItem: "Afficher {name}",
  hideItem: "Masquer {name}",
  duplicateItem: "Dupliquer {name}",
  deleteItem: "Supprimer {name}",
  removeItem: "Retirer {name}",
  reorderHint: "Faites glisser les sections, utilisez les flèches ou appuyez sur Alt + ↑/↓ sur une section pour la déplacer.",
  addSection: "Ajouter une section",
  sectionLimit: "Limite de sections atteinte ({max})",
  sectionTypes: "Types de sections",

  // Section settings form
  textareaHint: "Utilisez ## pour un titre et - pour les éléments de liste.",
  urlHint: "Une page de votre boutique comme /products ou /pages/about, ou une adresse https:// complète.",
  noCollectionsHint: "Vous n’avez pas encore de collection — les produits les plus récents sont affichés.",
  newestProducts: "Produits les plus récents",
  betweenHint: "Entre {min} et {max}.",
  change: "Changer",
  selectImage: "Choisir une image",
  selectImageHint: "Depuis votre médiathèque ou en important une nouvelle",
  itemN: "Élément {n}",
  noItems: "Aucun élément pour l’instant.",
  maxReached: "Maximum de {max} atteint",
  addItem: "Ajouter un élément",
  addItem_benefits: "Ajouter un avantage",
  addItem_testimonials: "Ajouter une citation",
  addItem_faq: "Ajouter une question",

  // Section registry
  section_hero: "Bannière principale",
  section_hero_desc: "Grand titre avec une image et un appel à l’action.",
  section_featured_products: "Produits en vedette",
  section_featured_products_desc: "Une grille de produits issus d’une collection ou de vos nouveautés.",
  section_featured_collections: "Liste de collections",
  section_featured_collections_desc: "Des liens vers vos collections publiées.",
  section_image_text: "Image avec texte",
  section_image_text_desc: "Racontez votre histoire à côté d’une image.",
  section_promo_banner: "Bannière promotionnelle",
  section_promo_banner_desc: "Un bandeau marquant pour mettre en avant une offre.",
  section_benefits: "Avantages",
  section_benefits_desc: "Mettez en avant vos engagements : livraison, retours, qualité ou service.",
  section_testimonials: "Témoignages",
  section_testimonials_desc: "Citations de vrais clients. N’ajoutez que des avis authentiques que vous êtes autorisé à partager.",
  section_rich_text: "Bloc de texte",
  section_rich_text_desc: "Titre et paragraphes. Mise en forme simple : ## pour les titres et - pour les listes.",
  section_newsletter: "Inscription à la newsletter",
  section_newsletter_desc: "Recueillez des abonnés consentants pour votre marketing par e-mail.",
  section_faq: "FAQ",
  section_faq_desc: "Questions fréquentes.",
  section_contact_info: "Coordonnées",
  section_contact_info_desc: "Affiche les coordonnées définies dans les paramètres de votre boutique.",
  section_contact_form: "Formulaire de contact",
  section_contact_form_desc: "Vos clients peuvent vous envoyer un message par e-mail.",
  field_heading: "Titre",
  field_subheading: "Sous-titre",
  field_buttonLabel: "Texte du bouton",
  field_buttonLink: "Lien du bouton",
  field_imageMediaId: "Image",
  field_hero_imageMediaId: "Image de fond",
  field_alignment: "Alignement",
  field_height: "Hauteur",
  field_overlay: "Assombrir l’image pour la lisibilité",
  field_collectionId: "Collection (facultatif)",
  field_featured_products_limit: "Nombre de produits",
  field_featured_collections_limit: "Nombre de collections",
  field_columns: "Colonnes sur ordinateur",
  field_text: "Texte",
  field_contact_info_text: "Texte d’introduction",
  field_imagePosition: "Position de l’image",
  field_style: "Style",
  field_benefits_items: "Avantages",
  field_testimonials_items: "Citations",
  field_faq_items: "Questions",
  field_showEmail: "Afficher l’e-mail",
  field_showPhone: "Afficher le téléphone",
  field_showAddress: "Afficher l’adresse",
  itemField_title: "Titre",
  itemField_text: "Texte",
  itemField_quote: "Citation",
  itemField_author: "Nom du client",
  itemField_question: "Question",
  itemField_answer: "Réponse",
  opt_alignment_left: "Gauche",
  opt_alignment_center: "Centré",
  opt_height_small: "Petite",
  opt_height_medium: "Moyenne",
  opt_height_large: "Grande",
  opt_imagePosition_left: "Gauche",
  opt_imagePosition_right: "Droite",
  opt_style_primary: "Couleur de marque",
  opt_style_dark: "Sombre",
  opt_style_light: "Clair",

  // Theme panel
  pickTheme: "Choisissez un design de départ. Vous pourrez affiner les couleurs et les polices dans Styles.",
  premium: "Premium",
  current: "Actuel",
  useTheme: "Utiliser {name}",
  switchToTheme: "Passer à {name} ?",
  switchTheme: "Changer de thème",
  switchThemeBody: "Vos personnalisations de style (couleurs, polices, options de mise en page) seront remplacées par les valeurs par défaut de {name}. Vos pages, produits et menus sont conservés.",
  premiumThemeNote: "{name} est un thème premium qui nécessite une offre payante.",
  resetHome: "Réinitialiser aussi la mise en page de l’accueil",
  resetHomeHint: "Remplace les sections de votre page d’accueil par la mise en page recommandée de {name}.",
  resetHomeSeeAfter: "Vous la verrez après le changement.",
  nothingChangesUntilPublish: "Rien ne change sur votre boutique en ligne tant que vous n’avez pas publié.",
  theme_aurora_category: "Commerce généraliste",
  theme_aurora_desc: "Une mise en page épurée et polyvalente, adaptée à presque tous les catalogues.",
  theme_atelier_category: "Mode et vêtements",
  theme_atelier_desc: "Typographie éditoriale et images en portrait pour les vêtements et accessoires.",
  theme_voltage_category: "Électronique",
  theme_voltage_desc: "Mise en page contrastée, idéale pour les fiches techniques, gadgets et produits high-tech.",
  theme_bloom_category: "Beauté et cosmétiques",
  theme_bloom_desc: "Palette douce et formes arrondies pour les marques de soins et de beauté.",
  theme_hearth_category: "Maison et mobilier",
  theme_hearth_desc: "Tons neutres chaleureux et grandes images pour la décoration et l’équipement de la maison.",
  theme_harvest_category: "Alimentation et épicerie fine",
  theme_harvest_desc: "Un design convivial et appétissant pour l’alimentation, les boissons et les épiceries fines.",
  theme_pixel_category: "Produits numériques",
  theme_pixel_desc: "Mise en page minimaliste et rapide pour les téléchargements, modèles et formations.",

  // Styles panel
  logoFaviconIn: "Le logo et le favicon se règlent dans",
  errHexColor: "Utilisez une couleur hexadécimale à 6 chiffres, par ex. #1a2b3c",
  colorPicker: "Sélecteur pour {name}",
  styleUrlHint: "Une adresse https:// complète, ou un chemin de votre boutique comme /products.",
  styleGroup_colors: "Couleurs",
  styleGroup_typography: "Typographie",
  styleGroup_layout: "Mise en page",
  styleGroup_headerFooter: "En-tête et pied de page",
  styleGroup_products: "Produits",
  style_colorPrimary: "Couleur de marque",
  style_colorPrimaryText: "Texte sur la couleur de marque",
  style_colorBackground: "Arrière-plan de la page",
  style_colorSurface: "Cartes et panneaux",
  style_colorText: "Texte",
  style_colorMuted: "Texte secondaire",
  style_colorAccent: "Promotions et mise en avant",
  style_fontHeading: "Police des titres",
  style_fontBody: "Police du texte",
  style_radius: "Arrondi des angles",
  style_buttonStyle: "Boutons",
  style_containerWidth: "Largeur du contenu",
  style_spacing: "Espacement",
  style_headerLayout: "Disposition de l’en-tête",
  style_stickyHeader: "En-tête fixe",
  style_announcementText: "Texte de la barre d’annonce",
  style_announcementLink: "Lien de l’annonce",
  style_footerText: "Texte du pied de page",
  style_socialInstagram: "URL Instagram",
  style_socialFacebook: "URL Facebook",
  style_socialX: "URL X",
  style_socialTiktok: "URL TikTok",
  style_productImageRatio: "Format des images produit",
  style_productCardStyle: "Carte produit",
  style_showVendor: "Afficher la marque/le fournisseur sur les cartes",
  style_productPageLayout: "Disposition de la page produit",
  style_collectionColumns: "Colonnes du catalogue (ordinateur)",
  font_system: "Sans empattement système",
  font_serif: "Avec empattement système",
  opt_radius_none: "Carré",
  opt_radius_small: "Petit",
  opt_radius_medium: "Moyen",
  opt_radius_large: "Grand",
  opt_buttonStyle_solid: "Plein",
  opt_buttonStyle_outline: "Contour",
  opt_buttonStyle_pill: "Pilule",
  opt_containerWidth_narrow: "Étroite",
  opt_containerWidth_normal: "Normale",
  opt_containerWidth_wide: "Large",
  opt_spacing_compact: "Compact",
  opt_spacing_comfortable: "Confortable",
  opt_spacing_airy: "Aéré",
  opt_headerLayout_left: "Logo à gauche",
  opt_headerLayout_center: "Logo centré",
  opt_productImageRatio_square: "Carré",
  opt_productImageRatio_portrait: "Portrait",
  opt_productImageRatio_landscape: "Paysage",
  opt_productCardStyle_plain: "Simple",
  opt_productCardStyle_bordered: "Avec bordure",
  opt_productCardStyle_shadow: "Avec ombre",
  opt_productPageLayout_side: "Galerie à côté des détails",
  opt_productPageLayout_stacked: "Galerie au-dessus des détails",

  // Pages
  pagesDesc: "Votre page d’accueil, les politiques de la boutique et toute page supplémentaire comme À propos ou FAQ. Les modifications restent en brouillon jusqu’à la publication.",
  noPagesDesc: "Créez votre première page — par exemple À propos ou Informations de livraison.",
  colPage: "Page",
  colUrl: "URL",
  colInNav: "Dans le menu",
  colOrder: "Ordre",
  homePage: "Page d’accueil",
  kind_home: "Accueil",
  kind_standard: "Standard",
  kind_policy: "Politique",
  kind_contact: "Contact",
  kind_faq: "FAQ",
  kind_about: "À propos",
  editInBuilder: "Modifier dans le constructeur",
  storePolicies: "Politiques de la boutique",
  storePoliciesDesc: "Les politiques de remboursement, de confidentialité, les conditions et la politique de livraison ont été créées à partir de modèles.",
  reviewed: "Vérifiées",
  policiesTemplatesNote: "Les modèles sont un point de départ, pas un avis juridique. Ils doivent être relus — idéalement par un professionnel du droit — pour correspondre à la façon dont votre entreprise gère réellement les remboursements, les données et la livraison, ainsi qu’aux lois des pays où vous vendez. Modifiez le texte de chaque politique dans l’onglet « Texte » du constructeur.",
  markPoliciesConfirmTitle: "Marquer les politiques comme vérifiées ?",
  markPoliciesConfirmBody: "Confirmez que vous avez lu et adapté chaque politique à votre activité. Cela met uniquement à jour votre liste de lancement — rien n’est publié.",
  markPoliciesReviewed: "Marquer les politiques comme vérifiées",
  liveUnpublishedChanges: "En ligne · modifications non publiées",

  // Page settings
  pageSettings: "Paramètres de la page",
  pageSettingsDesc: "Titre, adresse et apparence de la page dans les résultats de recherche.",
  kindPageAt: "Page {kind} à l’adresse",
  previewDraft: "Prévisualiser le brouillon ↗",
  revisions: "Révisions",
  revisionsDesc: "Une révision est enregistrée à chaque publication de cette page. La restauration la charge dans le brouillon — elle n’est mise en ligne qu’à la publication.",
  noRevisions: "Aucune révision pour l’instant",
  noRevisionsDesc: "Publiez la page pour créer la première révision.",
  latestPublished: "Dernière version publiée",
  revisionMeta: "« {title} » · {count} sections",
  revisionMeta_one: "« {title} » · {count} section",
  restoreConfirmTitle: "Restaurer cette révision dans le brouillon ?",
  restoreConfirmBody: "Le contenu actuel du brouillon sera remplacé par cette révision. La page en ligne ne change pas tant que vous n’avez pas publié.",
  restoreIntoDraft: "Restaurer dans le brouillon",
  restore: "Restaurer",
  publishing: "Publication",
  lastPublished: "Dernière publication",
  lastEdited: "Dernière modification",
  draftNotLive: "Le brouillon contient des modifications qui ne sont pas encore en ligne.",
  discardConfirmTitle: "Annuler les modifications du brouillon ?",
  discardConfirmBody: "Le brouillon revient à la version en ligne. Les modifications non publiées seront perdues.",
  discardDraft: "Annuler le brouillon",
  unpublishPageConfirmTitle: "Dépublier cette page ?",
  unpublishPageConfirmBody: "Les visiteurs ne la verront plus. Les liens vers cette page afficheront « introuvable ». Vous pouvez la republier à tout moment.",
  deletePage: "Supprimer la page",
  deletePageBody: "La suppression retire la page et ses révisions. Les liens de menu qui y mènent ne fonctionneront plus.",
  deletePageConfirmTitle: "Supprimer « {title} » ?",

  // Page forms
  homeAlwaysAt: "La page d’accueil se trouve toujours à l’adresse",
  pageWillBeAt: "Votre page sera à l’adresse",
  slugRedirectHint: "Si vous changez l’URL d’une page en ligne, les anciens liens continuent de fonctionner grâce à une redirection.",
  showInNav: "Afficher dans la navigation",
  showInNavHint: "Ajoute automatiquement cette page à l’en-tête de la boutique.",
  seoListing: "Référencement dans les moteurs de recherche",
  seoTitle: "Titre SEO",
  seoTitleHint: "Par défaut, le titre de la page.",
  seoDescription: "Description SEO",
  seoDescriptionHint: "Un court résumé affiché dans les résultats de recherche.",
  hideFromSearch: "Masquer aux moteurs de recherche",
  hideFromSearchHint: "Ajoute une balise noindex. À utiliser pour les pages de remerciement ou privées.",
  createPage: "Créer une page",
  createPageNote: "La page commence en brouillon. Vous la concevrez ensuite dans le constructeur, puis la publierez quand elle sera prête.",
  createAndEdit: "Créer et modifier",
  saveSettings: "Enregistrer les paramètres",

  // Navigation
  navDesc: "Choisissez les liens affichés dans l’en-tête et le pied de page de votre boutique. Les modifications de menu sont en ligne dès l’enregistrement.",
  groupStore: "Boutique",
  linksToDraftsTitle: "Certains liens pointent vers des pages non publiées",
  linksToDraftsBody: "{links} — les visiteurs verront « introuvable » tant que vous n’aurez pas publié ces pages.",
  linksToDraftsBody_one: "{links} — les visiteurs verront « introuvable » tant que vous n’aurez pas publié cette page.",
  mainMenu: "Menu principal",
  mainMenuDesc: "Affiché dans l’en-tête de chaque page.",
  footerMenu: "Menu du pied de page",
  footerMenuDesc: "Affiché en bas de chaque page — idéal pour les politiques et le contact.",
  errAddLabel: "Ajoutez un libellé",
  errChooseLink: "Choisissez un lien",
  errLinkFormat: "Utilisez un chemin comme /products ou une adresse https:// complète",
  errNetworkKept: "Impossible de joindre le serveur. Vos modifications sont toujours là — réessayez.",
  linkFallback: "lien",
  itemRemoved: "{name} retiré.",
  saveMenu: "Enregistrer le menu",
  noLinks: "Aucun lien pour l’instant. Ajoutez votre premier lien ci-dessous.",
  label: "Libellé",
  labelPh: "ex. Boutique",
  linkTo: "Lien vers",
  customUrl: "URL personnalisée…",
  urlPh: "/pages/about ou https://…",
  maxLinks: "Maximum de {max} liens",
  addLink: "Ajouter un lien",

  // Media
  mediaDesc: "Images pour les produits, les pages et les bannières, ainsi que les fichiers privés remis aux clients qui achètent des produits numériques.",
  tabImages: "Images",
  tabPrivate: "Fichiers privés",
  searchByFilename: "Rechercher par nom de fichier",
  searchImagesByFilename: "Rechercher des images par nom de fichier",
  missingAlt: "{count} images de cette page n’ont pas de texte alternatif. Le texte alternatif décrit les images pour les personnes utilisant un lecteur d’écran et aide les moteurs de recherche.",
  missingAlt_one: "{count} image de cette page n’a pas de texte alternatif. Le texte alternatif décrit les images pour les personnes utilisant un lecteur d’écran et aide les moteurs de recherche.",
  noFilesMatch: "Aucun fichier ne correspond à votre recherche",
  noImagesYet: "Aucune image pour l’instant",
  noPrivateYet: "Aucun fichier privé pour l’instant",
  noImagesDesc: "Importez des photos de produits, des bannières et d’autres images. Elles sont optimisées automatiquement.",
  noPrivateDesc: "Les fichiers privés (PDF, ZIP, EPUB, MP3, MP4) ne sont accessibles aux clients qu’au moyen de liens de téléchargement sécurisés après l’achat.",
  missingAltTitle: "Texte alternatif manquant",
  noAlt: "Sans alt",
  colFile: "Fichier",
  colSize: "Taille",
  colUploaded: "Importé le",
  added: "Ajouté le",
  deleteFileConfirmTitle: "Supprimer {name} ?",
  deleteFileConfirmBody: "Les fichiers associés à un produit ou déjà achetés par un client ne peuvent pas être supprimés.",
  deleteFile: "Supprimer le fichier",
  imageDetails: "Détails de l’image",
  closeDetails: "Fermer les détails",
  copyUrl: "Copier l’URL",
  altText: "Texte alternatif",
  altHint: "Décrivez le contenu de l’image, par ex. « Mug en céramique bleu sur une table en bois ». Ne laissez vide que pour les images décoratives.",
  saveAlt: "Enregistrer le texte alternatif",
  deleteImageConfirmBody: "Les images utilisées par un produit, une collection, une page ou comme logo ne peuvent pas être supprimées — retirez-les d’abord à ces endroits.",
  deleteImage: "Supprimer l’image",
  uploadImages: "Importer des images",
  uploadPrivate: "Importer des fichiers privés",
  uploadImagesHint: "Les images sont publiques afin de pouvoir apparaître sur votre boutique. Sélectionnez une image pour ajouter un texte alternatif.",
  uploadPrivateHint: "Les fichiers privés n’ont jamais d’URL publique. Associez-les à un produit numérique pour que les acheteurs reçoivent des liens de téléchargement sécurisés et temporaires.",
  selectImageDetailsHint: "Sélectionnez une image pour voir ses détails, modifier son texte alternatif ou la supprimer.",
  filesUploaded: "{count} fichiers importés.",
  filesUploaded_one: "{count} fichier importé.",

  // Media picker
  chooseImage: "Choisir une image",
  uploadNewImage: "Importer une nouvelle image",
  errLoadImages: "Impossible de charger les images.",
  errNetwork: "Erreur réseau — vérifiez votre connexion et réessayez.",
  loadingImages: "Chargement des images",
  noImagesMatch: "Aucune image ne correspond à votre recherche.",
  libraryEmpty: "Votre médiathèque est vide. Importez une image ci-dessus.",
  imagePages: "Pages d’images",
};

export default fr;
